"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const classnames_1 = __importDefault(require("classnames"));
const prop_types_1 = __importDefault(require("prop-types"));
const pattern_library_hooks_use_breakpoints_1 = require("@saatva-bits/pattern-library.hooks.use-breakpoints");
const selection_types_1 = require("../../../selection.types");
const ProductSelectSwatch_1 = __importDefault(require("./ProductSelectSwatch/ProductSelectSwatch"));
const ProductSelectToggle_1 = __importDefault(require("./ProductSelectToggle/ProductSelectToggle"));
const ProductSelectDropdown_1 = __importDefault(require("./ProductSelectDropdown/ProductSelectDropdown"));
const ProductSelectCheckbox_1 = __importDefault(require("./ProductSelectCheckbox/ProductSelectCheckbox"));
const styles_module_scss_1 = __importDefault(require("./styles.module.scss"));
/**
 *
 * @param {Object} props
 * @param {string} props.productCode - The product sku to retrieve and show all attribute selectors
 * @param {{
 *  code: string,
 *  label: string,
 *  displayType: string,
 *  canScroll: boolean
 * }[]} props.attributeOptions - Contains the array of configurable attributes that can be selected by the user
 * @param {('small' | 'medium')} props.swatchesSize - Swatches size for the default swatch selector
 * @param {string} props.dataSelectorModifier
 * @param {(props: Object) => import('react').ReactNode} props.SwatchComponent - Component to render instead of the default swatch selector
 * @param {(props: Object) => import('react').ReactNode} props.CheckboxComponent - Component to render instead of the default checkbox selector
 * @param {(props: Object) => import('react').ReactNode} props.ToggleComponent - Component to render instead of the default toggle selector
 * @param {(props: Object) => import('react').ReactNode} props.DropdownComponent - Component to render instead of the default Dropdown selector
 * @param {Object} props.epigraphArButtonProps - Object that contains Epigraph components to render
 * @param {import('react').ReactNode} props.epigraphArButtonProps.EpigraphModelViewer - Epigraph Model Viewer Component
 * @param {import('react').ReactNode} props.epigraphArButtonProps.EpigraphModelDisclaimer - Epigraph Model Disclaimer Component
 * @param {string} props.containerClassName - A custom class name for the product selectors container
 * @param {boolean} props.isOutOfStockStyling - Show strikethroughs if the relevant variant is out of stock
 * @returns {import('react').ReactNode}
 */
const SelectorsWrapper = ({ productCode, attributeOptions, swatchesSize, dataSelectorModifier, SwatchComponent, CheckboxComponent, ToggleComponent, DropdownComponent, epigraphArButtonProps, containerClassName, isOutOfStockStyling }) => {
    if (!productCode || !attributeOptions) {
        return null;
    }
    const { isMobile } = (0, pattern_library_hooks_use_breakpoints_1.useBreakpoints)('mobile');
    const hasSize = Boolean(attributeOptions.find((attribute) => attribute.code === 'size'));
    const hasQty = Boolean(attributeOptions.find((attribute) => attribute.code === 'quantity'));
    const hasColor = Boolean(attributeOptions.find((attribute) => attribute.code === 'color'));
    const hasScent = Boolean(attributeOptions.find((attribute) => attribute.code === 'scent'));
    const hasOnlyQty = hasQty && attributeOptions.length === 1;
    const changeOrder = !hasSize && hasQty && (hasColor || hasScent);
    const stackingQtySelector = isMobile && hasScent;
    const parentClassName = (0, classnames_1.default)('u-paddingTop--xs', {
        [styles_module_scss_1.default['productSelect-inlineElements']]: changeOrder,
        [styles_module_scss_1.default.stackingScent]: stackingQtySelector,
        [`${containerClassName}`]: containerClassName
    });
    return ((0, jsx_runtime_1.jsx)("div", { className: parentClassName, children: attributeOptions.map(({ code, displayType, values, toggleProps, canScroll = false }, i) => {
            // if the qty dropdown comes after the size dropdown, we want them to be on the same line
            const nextOption = i + 1 < attributeOptions.length && attributeOptions[i + 1];
            const isReducedDropdownWidth = code === 'size' && (nextOption === null || nextOption === void 0 ? void 0 : nextOption.code) === 'quantity';
            const codeSelectStyle = styles_module_scss_1.default[`${code}Select`];
            const hasCodeSelectStyle = Boolean(codeSelectStyle);
            const className = (0, classnames_1.default)(styles_module_scss_1.default.productSelect, {
                [codeSelectStyle]: hasCodeSelectStyle,
                [`${styles_module_scss_1.default[`${code}Select-hasQuantity`]}`]: hasCodeSelectStyle && isReducedDropdownWidth,
                [`${styles_module_scss_1.default[`${code}Select-changeOrder`]}`]: hasCodeSelectStyle && changeOrder,
                [styles_module_scss_1.default['quantitySelect--hasOnlyQty']]: hasOnlyQty,
                [styles_module_scss_1.default.toggleSelect]: displayType === 'toggle',
                [`${styles_module_scss_1.default[`toggleSelect--autoFitColumnLayout`]}`]: displayType === 'toggle' && values.length < 3 || (toggleProps === null || toggleProps === void 0 ? void 0 : toggleProps.autoFitColumnLayoutOverride),
                [`${containerClassName}__${displayType}Selector`]: containerClassName
            });
            const displayPrice = code === 'size';
            const defaultDataSelector = `${code}Selector-${productCode}`;
            const modifiedDataSelector = dataSelectorModifier && `${defaultDataSelector}-${dataSelectorModifier}`;
            const props = {
                index: i,
                key: `product-select-${code}-${i}`,
                attributeCode: code,
                className,
                displayPrice,
                productCode,
                toggleProps,
                dataSelector: modifiedDataSelector || defaultDataSelector,
                canScroll,
                size: swatchesSize,
                isOutOfStockStyling
            };
            switch (displayType) {
                case 'swatch':
                    if (SwatchComponent)
                        return (0, jsx_runtime_1.jsx)(SwatchComponent, { ...props });
                    return (0, jsx_runtime_1.jsx)(ProductSelectSwatch_1.default, { ...props, epigraphArButtonProps: epigraphArButtonProps });
                case 'checkbox':
                    if (CheckboxComponent)
                        return (0, jsx_runtime_1.jsx)(CheckboxComponent, { ...props });
                    return (0, jsx_runtime_1.jsx)(ProductSelectCheckbox_1.default, { ...props });
                case 'toggle':
                    if (ToggleComponent)
                        return (0, jsx_runtime_1.jsx)(ToggleComponent, { ...props });
                    return (0, jsx_runtime_1.jsx)(ProductSelectToggle_1.default, { ...props });
                case 'dropdown':
                default:
                    if (DropdownComponent)
                        return (0, jsx_runtime_1.jsx)(DropdownComponent, { ...props });
                    return (0, jsx_runtime_1.jsx)(ProductSelectDropdown_1.default, { ...props });
            }
        }) }));
};
SelectorsWrapper.propTypes = {
    productCode: prop_types_1.default.string.isRequired,
    swatchesSize: prop_types_1.default.string,
    dataSelectorModifier: prop_types_1.default.string,
    /** Component to render instead of the default swatch selector */
    SwatchComponent: prop_types_1.default.node,
    /** Component to render instead of the default checkbox selector */
    CheckboxComponent: prop_types_1.default.node,
    /** Component to render instead of the default toggle selector */
    ToggleComponent: prop_types_1.default.node,
    /** Component to render instead of the default Dropdown selector */
    DropdownComponent: prop_types_1.default.node,
    attributeOptions: prop_types_1.default.arrayOf(prop_types_1.default.shape({
        code: prop_types_1.default.string.isRequired,
        displayType: prop_types_1.default.string.isRequired,
        togglePropsType: selection_types_1.toggleProps,
        canScroll: prop_types_1.default.bool,
        fullHeight: prop_types_1.default.bool
    })).isRequired,
    /** Epigraph model viewer and disclaimer passed to the selectors */
    epigraphArButtonProps: prop_types_1.default.shape({
        EpigraphModelViewer: prop_types_1.default.node,
        EpigraphDisclaimer: prop_types_1.default.node
    }),
    containerClassName: prop_types_1.default.string,
    isOutOfStockStyling: prop_types_1.default.bool
};
exports.default = SelectorsWrapper;
//# sourceMappingURL=SelectorsWrapper.js.map